/** API endpoints and their response schemas (services/api). */
import { z } from 'zod';
import { api } from './http.ts';

// zod must not try `new Function` (blocked by the App's CSP, design §8.3).
z.config({ jitless: true });

const nothing = z.null();

// --- auth ---------------------------------------------------------------------------------

export const meSchema = z.object({
  login_id: z.string(),
  role: z.enum(['learner', 'admin', 'staff']),
  must_change_password: z.boolean(),
});
export type Me = z.infer<typeof meSchema>;

export const auth = {
  me: () => api('/api/auth/me', meSchema),
  login: (body: { login_id: string; password: string; totp?: string }) =>
    api('/api/auth/login', meSchema, { method: 'POST', body }),
  logout: () => api('/api/auth/logout', nothing, { method: 'POST' }),
  changePassword: (body: { current_password: string; new_password: string }) =>
    api('/api/auth/password', nothing, { method: 'POST', body }),
};

// --- progress -----------------------------------------------------------------------------

const lessonStatus = z.enum(['not_started', 'in_progress', 'passed']);

export const lessonStateSchema = z.object({
  lesson_id: z.string(),
  status: lessonStatus,
  passed_at: z.string().nullable(),
  steps: z.record(
    z.string(),
    z.object({
      hint_level_used: z.number().int().min(0).max(3),
      prediction: z.number().int().nullable(),
      answers: z.array(z.number().int()).nullable(),
      completed_at: z.string(),
    }),
  ),
  code: z.record(z.string(), z.string()),
});
export type LessonState = z.infer<typeof lessonStateSchema>;

export const progressListSchema = z.object({
  lessons: z.array(
    z.object({
      lesson_id: z.string(),
      status: lessonStatus,
      passed_at: z.string().nullable(),
      completed_steps: z.number().int(),
    }),
  ),
});

export interface StepDone {
  hint_level_used: number;
  prediction?: number;
  answers?: number[];
}

export interface Attempt {
  lesson_id: string;
  step_id: string;
  kind: 'predict' | 'investigate' | 'test';
  passed: boolean;
  hint_level_used: number;
  duration_ms?: number;
}

const enc = encodeURIComponent;

export const progress = {
  list: () => api('/api/progress', progressListSchema),
  lesson: (lessonId: string) => api(`/api/progress/${enc(lessonId)}`, lessonStateSchema),
  completeStep: (lessonId: string, stepId: string, body: StepDone) =>
    api(`/api/progress/${enc(lessonId)}/steps/${enc(stepId)}`, z.object({ status: lessonStatus }), {
      method: 'PUT',
      body,
    }),
  saveCode: (lessonId: string, stepId: string, code: string, keepalive = false) =>
    api(`/api/code/${enc(lessonId)}/${enc(stepId)}`, nothing, {
      method: 'PUT',
      body: { code },
      keepalive,
    }),
  attempt: (body: Attempt) =>
    api('/api/attempts', z.object({ ok: z.boolean() }), { method: 'POST', body }),
  /** The model answer of a code step the learner has passed (ADR-0008); 404 before that. */
  solution: (lessonId: string, stepId: string) =>
    api(
      `/api/progress/${enc(lessonId)}/steps/${enc(stepId)}/solution`,
      z.object({ code: z.string() }),
    ),
};

// --- admin --------------------------------------------------------------------------------

export const learnerListSchema = z.object({
  learners: z.array(
    z.object({
      login_id: z.string(),
      role: z.string(),
      disabled: z.boolean(),
      created_at: z.string(),
      lessons_passed: z.number().int(),
      last_activity: z.string().nullable(),
      steps_by_lesson: z.record(z.string(), z.number().int().nonnegative()),
      last_lesson_id: z.string().nullable(),
    }),
  ),
});
export type LearnerList = z.infer<typeof learnerListSchema>;

export const learnerDetailSchema = z.object({
  login_id: z.string(),
  lessons: z.array(
    z.object({
      lesson_id: z.string(),
      status: z.string(),
      passed_at: z.string().nullable(),
      steps: z.array(
        z.object({
          step_id: z.string(),
          hint_level_used: z.number().int(),
          completed_at: z.string(),
        }),
      ),
    }),
  ),
  code: z.array(z.object({ lesson_id: z.string(), step_id: z.string(), updated_at: z.string() })),
});
export type LearnerDetail = z.infer<typeof learnerDetailSchema>;

export const codeViewSchema = z.object({
  login_id: z.string(),
  lesson_id: z.string(),
  step_id: z.string(),
  code: z.string(),
  updated_at: z.string(),
});

export const admin = {
  learners: () => api('/api/admin/learners', learnerListSchema),
  learner: (loginId: string) => api(`/api/admin/learners/${enc(loginId)}`, learnerDetailSchema),
  code: (loginId: string, lessonId: string, stepId: string) =>
    // POST: opening code is recorded, so it goes through the API's CSRF guard.
    api(
      `/api/admin/learners/${enc(loginId)}/code/${enc(lessonId)}/${enc(stepId)}`,
      codeViewSchema,
      {
        method: 'POST',
      },
    ),
};

// --- CS staff (ADR-0009) --------------------------------------------------------------------

export const staffSettingsSchema = z.object({ issue_limit: z.boolean(), notify: z.boolean() });
export type StaffSettings = z.infer<typeof staffSettingsSchema>;
export type StaffSetting = keyof StaffSettings;

export const adminStaffSettings = {
  get: () => api('/api/admin/staff-settings', staffSettingsSchema),
  /** ON only: switching off is done on the VM (plsys-admin staff-settings off). */
  on: (setting: StaffSetting) =>
    api(`/api/admin/staff-settings/${enc(setting)}/on`, staffSettingsSchema, { method: 'POST' }),
};

const learnerAccountsSchema = z.object({
  learners: z.array(
    z.object({
      login_id: z.string(),
      disabled: z.boolean(),
      must_change_password: z.boolean(),
      created_at: z.string(),
    }),
  ),
});
export type LearnerAccounts = z.infer<typeof learnerAccountsSchema>;
const issuedAccountSchema = z.object({ login_id: z.string(), initial_password: z.string() });
export type IssuedAccount = z.infer<typeof issuedAccountSchema>;

export const staff = {
  learners: () => api('/api/staff/learners', learnerAccountsSchema),
  issue: (loginIds: string[], totp: string) =>
    api('/api/staff/learners', z.object({ issued: z.array(issuedAccountSchema) }), {
      method: 'POST',
      body: { login_ids: loginIds, totp },
    }),
  resetPassword: (loginId: string, totp: string) =>
    api(`/api/staff/learners/${enc(loginId)}/password`, issuedAccountSchema, {
      method: 'POST',
      body: { totp },
    }),
  setDisabled: (loginId: string, disabled: boolean, totp: string) =>
    api(`/api/staff/learners/${enc(loginId)}/disabled`, nothing, {
      method: 'POST',
      body: { disabled, totp },
    }),
};

// --- review (solving completed steps again, 2026-10-09) -------------------------------------

const reviewListSchema = z.object({
  steps: z.array(
    z.object({ lesson_id: z.string(), step_id: z.string(), reviewed_at: z.string().nullable() }),
  ),
});
export type ReviewList = z.infer<typeof reviewListSchema>;

export const review = {
  /** Completed steps the learner got wrong at least once. */
  list: () => api('/api/review', reviewListSchema),
  /** Solved again correctly. */
  mark: (lessonId: string, stepId: string) =>
    api(`/api/review/${enc(lessonId)}/${enc(stepId)}`, nothing, { method: 'PUT' }),
};
